import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "../i18n/LocaleContext";

export function ProductShowcaseCarousel() {
  const { locale } = useLocale();
  const slides = useMemo(() => {
    const copy = {
      en: [
        {
          title: "ROUND LAB",
          subtitle: "A calm Korean skincare visual centered on the Dokdo range.",
          image: "/official-brands/roundlab.jpg",
          accent: "Signature skincare",
        },
        {
          title: "CNP",
          subtitle: "Derma-care image built around clinical skincare credibility.",
          image: "/official-brands/cnp-toner.png",
          accent: "Derma skincare",
        },
        {
          title: "ILSO",
          subtitle: "A refined brand image designed for clean category presentation.",
          image: "/yomei-materials/ilso-book.png",
          accent: "Brand signature",
        },
        {
          title: "SUNGBOON EDITOR",
          subtitle: "Ingredient-led skincare presented through a cleaner editorial frame.",
          image: "/yomei-materials/sungboon-book.png",
          accent: "Ingredient skincare",
        },
        {
          title: "melomeli",
          subtitle: "QIANDU's owned beauty line presented through a calmer cleansing-water visual.",
          image: "/melomeli-water-02.jpg",
          accent: "Owned beauty line",
        },
      ],
      zh: [
        {
          title: "ROUND LAB",
          subtitle: "以 Dokdo 系列为核心的韩国护肤品牌展示。",
          image: "/official-brands/roundlab.jpg",
          accent: "代表护肤品牌",
        },
        {
          title: "CNP",
          subtitle: "以皮肤科学与功效护理为核心的品牌视觉。",
          image: "/official-brands/cnp-toner.png",
          accent: "德玛护肤",
        },
        {
          title: "ILSO",
          subtitle: "以简洁高级的视觉语气呈现品牌形象。",
          image: "/yomei-materials/ilso-book.png",
          accent: "品牌形象",
        },
        {
          title: "SUNGBOON EDITOR",
          subtitle: "以成分导向和更干净的品牌册视觉呈现护肤品牌形象。",
          image: "/yomei-materials/sungboon-book.png",
          accent: "成分护肤",
        },
        {
          title: "melomeli",
          subtitle: "以更干净的卸妆水视觉呈现千渡自有品牌。",
          image: "/melomeli-water-02.jpg",
          accent: "自有品牌",
        },
      ],
      ko: [
        {
          title: "ROUND LAB",
          subtitle: "독도 라인을 중심으로 한 한국 스킨케어 이미지를 보여줍니다.",
          image: "/official-brands/roundlab.jpg",
          accent: "대표 스킨케어",
        },
        {
          title: "CNP",
          subtitle: "더마 스킨케어 신뢰도를 중심으로 한 브랜드 이미지.",
          image: "/official-brands/cnp-toner.png",
          accent: "더마 스킨케어",
        },
        {
          title: "ILSO",
          subtitle: "정제된 브랜드 무드와 클린 카테고리 이미지를 전달합니다.",
          image: "/yomei-materials/ilso-book.png",
          accent: "브랜드 시그니처",
        },
        {
          title: "SUNGBOON EDITOR",
          subtitle: "성분 중심 스킨케어를 더 차분한 에디토리얼 무드로 보여줍니다.",
          image: "/yomei-materials/sungboon-book.png",
          accent: "성분 스킨케어",
        },
        {
          title: "melomeli",
          subtitle: "클렌징 워터 비주얼을 통해 QIANDU의 자사 브랜드를 차분하게 보여줍니다.",
          image: "/melomeli-water-02.jpg",
          accent: "자사 브랜드",
        },
      ],
      vi: [
        {
          title: "ROUND LAB",
          subtitle: "Hình ảnh skincare Hàn Quốc tập trung vào dòng Dokdo.",
          image: "/official-brands/roundlab.jpg",
          accent: "Skincare chủ lực",
        },
        {
          title: "CNP",
          subtitle: "Hình ảnh derma-care dựa trên uy tín chăm sóc da khoa học.",
          image: "/official-brands/cnp-toner.png",
          accent: "Derma skincare",
        },
        {
          title: "ILSO",
          subtitle: "Hình ảnh thương hiệu tinh gọn với ngôn ngữ clean beauty.",
          image: "/yomei-materials/ilso-book.png",
          accent: "Dấu ấn thương hiệu",
        },
        {
          title: "SUNGBOON EDITOR",
          subtitle: "Skincare định hướng thành phần với ngôn ngữ hình ảnh sạch và rõ ràng hơn.",
          image: "/yomei-materials/sungboon-book.png",
          accent: "Skincare thành phần",
        },
        {
          title: "melomeli",
          subtitle: "Dòng thương hiệu sở hữu của QIANDU được thể hiện bằng hình ảnh cleansing water tinh gọn hơn.",
          image: "/melomeli-water-02.jpg",
          accent: "Thương hiệu sở hữu",
        },
      ],
    } as const;

    return copy[locale];
  }, [locale]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex];
  const activeSlideUsesContainedVisual =
    activeSlide.image.endsWith(".svg") ||
    activeSlide.image.includes("/yomei-materials/") ||
    activeSlide.image.includes("-logo") ||
    activeSlide.image.includes("cnp-toner");

  useEffect(() => {
    setActiveIndex(0);
  }, [slides]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, 4200);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="grid gap-4 lg:grid-cols-[1.58fr_0.42fr] lg:items-stretch">
      <div className="relative overflow-hidden rounded-[2.75rem] border border-[#dce9ef] bg-[linear-gradient(180deg,rgba(253,255,255,0.92),rgba(239,246,249,0.95))] shadow-[0_14px_28px_rgba(106,138,151,0.07)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSlide.image}
            initial={{ opacity: 0.35, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0.25, scale: 0.985 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative aspect-[1/1] sm:aspect-[1.58/0.88]"
          >
            <img
              src={activeSlide.image}
              alt={activeSlide.title}
              className={`h-full w-full ${activeSlideUsesContainedVisual ? "object-contain bg-[rgba(246,249,250,0.94)] p-8 sm:p-12" : "object-cover object-center"}`}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(47,78,92,0.08)_54%,rgba(30,44,52,0.38))]" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
              <div className="inline-flex rounded-full border border-white/20 bg-[rgba(230,243,248,0.14)] px-3 py-1 text-[7px] uppercase tracking-[0.38em] text-[#f6fbfd] backdrop-blur">
                {activeSlide.accent}
              </div>
              <h3 className="mt-4 max-w-xl font-display text-[1.68rem] leading-tight text-white sm:text-[2.12rem]">
                {activeSlide.title}
              </h3>
              <p className="mt-3 max-w-[12.5rem] text-[0.8rem] leading-6 text-white/68">{activeSlide.subtitle}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        {slides.map((slide, index) => {
          const usesContainedThumb =
            slide.image.endsWith(".svg") ||
            slide.image.includes("/yomei-materials/") ||
            slide.image.includes("-logo") ||
            slide.image.includes("cnp-toner");

          return (
          <button
            key={slide.title}
            type="button"
            onClick={() => setActiveIndex(index)}
            className={`rounded-[1.8rem] border p-3.5 text-left transition ${
              activeIndex === index
                ? "border-[#d4e4eb] bg-[linear-gradient(180deg,rgba(252,254,255,0.98),rgba(240,246,249,0.98))] shadow-[0_10px_20px_rgba(106,138,151,0.06)]"
                : "border-transparent bg-[rgba(248,252,254,0.62)] hover:bg-[rgba(251,254,255,0.86)]"
            }`}
          >
            <div className="flex items-start gap-4">
              <img
                src={slide.image}
                alt={slide.title}
                className={`h-[4.5rem] w-[4.5rem] rounded-[1rem] shadow-[0_10px_24px_rgba(82,70,56,0.08)] sm:h-20 sm:w-20 ${
                  usesContainedThumb ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))] object-contain p-3" : "object-cover object-center"
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="inline-flex rounded-full bg-[#edf6f9] px-2.5 py-1 text-[7px] uppercase tracking-[0.34em] text-soft/50">
                  0{index + 1}
                </div>
                <div className="mt-3 font-display text-[0.9rem] leading-6 text-[#1f2b31]">{slide.title}</div>
                <div className={`mt-2 line-clamp-2 text-[7px] uppercase tracking-[0.34em] ${activeIndex === index ? "text-soft/56" : "text-soft/42"}`}>
                  {slide.accent}
                </div>
              </div>
            </div>
          </button>
          );
        })}
      </div>
    </div>
  );
}
